/**
 * LocaleServiceCls: a service class to provide support for locale/i18n stuff.
 */

import i18next from '../i18n';
import { INSTANCE_DEFAULT_CURRENCY_CODE } from '../utils/instanceConstant';

class LocaleServiceCls {
  #optionsCache = new Map();

  /**
   * Get a currency formatting Intl.NumberFormat object.
   * TODO:i18n -> currency params!!!
   */
  getLocaleCurrencyFormatter(options = null, locale = null) {
    if (!options) {
      options = {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      };
    }
    options.style = 'currency';
    if (!options.currency) {
      // TODO:i18n - currency code should come from org etc
      options.currency = INSTANCE_DEFAULT_CURRENCY_CODE;
    }
    options.currencyDisplay = 'narrowSymbol';

    return this.#getFormatter('currency', locale, options);
  }

  /**
   * Format locale-based currency.
   *
   *  @param amt
   *  @param options - Intl.NumberFormat compatible options
   *  @locale - if null then current i18next language is used
   */
  fmtLocaleCurrency(amt, options, locale) {
    const fmt = this.getLocaleCurrencyFormatter(options, locale);
    return fmt.format(amt);
  }

  /**
   * Format locale-based currency (whole dollars).
   *
   *  @param amt
   *  @param options - Intl.NumberFormat compatible options
   *  @locale - if null then current i18next language is used
   */
  fmtLocaleCurrencyWholeDollars(amt, options, locale) {
    options = options || {};
    options.minimumFractionDigits = 0;
    options.maximumFractionDigits = 0;
    const fmt = this.getLocaleCurrencyFormatter(options, locale);
    return fmt.format(amt);
  }

  /**
   * Format locale-based currency in compact form ($2.14M / $ 2,14 mln.).
   *
   *  @param amt
   *  @param options - Intl.NumberFormat compatible options
   *  @locale - if null then current i18next language is used
   */
  fmtLocaleCurrencyCompact(amt, options, locale) {
    const v = Math.round(Number(amt) || 0);
    const abs = Math.abs(v);
    if (abs < 1_000) {
      return this.fmtLocaleCurrencyWholeDollars(v, { ...options }, locale);
    }

    let digits = 1;
    if (abs >= 1_000_000) {
      digits = 2;
    } else if (abs >= 10_000) {
      digits = 0;
    }

    return this.fmtLocaleCurrency(
      v,
      {
        ...options,
        notation: 'compact',
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      },
      locale
    );
  }

  /**
   * Get a numeral (whole number) formatting Intl.NumberFormat object.
   */
  getLocaleNumeralFormatter(
    options = {
      style: 'decimal',
      maximumFractionDigits: 0,
    },
    locale = null
  ) {
    return this.#getFormatter('numeral', locale, options);
  }

  /**
   * Format a locale-based numeral.
   *
   *  @param num
   *  @param options - Intl.NumberFormat compatible options
   *  @locale - if null then current i18next language is used
   */
  fmtLocaleNumeral(num, options, locale) {
    const fmt = this.getLocaleNumeralFormatter(options, locale);
    return fmt.format(num);
  }

  /**
   * Get a locale-based date formatting object,
   * for use with Date.toLocaleDateString()
   */
  getLocaleDateFormatter(options, locale = null) {
    return this.#getFormatter('date', locale, options);
  }

  /**
   * Format a locale-based date.
   *
   *  @param dt
   *  @param options - Intl.DateTimeFormat compatible options
   *  @locale - if null then current i18next language is used
   */
  fmtLocaleDate(dt, options, locale = null) {
    if (!dt) {
      return null;
    }
    const fmt = this.getLocaleDateFormatter(options, locale);
    if (!fmt) {
      return '';
    }

    dt = new Date(dt);
    if (!dt || isNaN(dt.getTime())) {
      return null;
    }

    return fmt.format(dt);
  }

  /*
  |--------------------------------------------------------------------------
  | Local helpers
  |--------------------------------------------------------------------------
  */

  #getLocale(locale) {
    return locale || i18next.language;
  }

  #getFormatter(what, locale, options) {
    locale = this.#getLocale(locale);
    if (!locale) {
      return null;
    }

    const key = `${what}--${locale}--${JSON.stringify(options)}`;
    if (!this.#optionsCache.has(key)) {
      if (['currency', 'numeral'].includes(what)) {
        this.#optionsCache.set(key, new Intl.NumberFormat(locale, options));
      } else {
        this.#optionsCache.set(key, new Intl.DateTimeFormat(locale, options));
      }
    }
    return this.#optionsCache.get(key);
  }
}

// service class is a singleton
const LocaleService = new LocaleServiceCls();

export { LocaleService };
